import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import axios from "../../../../axios_config";
import AnswerEmojiIcon from "../../../../common/assets/icons/user_profile_page_icons/answer_emoji_icon";
import { BASE_API_URL } from "../../../../common/endpoints";
import useOnclickOutside from "react-cool-onclickoutside";

const AnswerPickerContainer = styled.div`
  position: absolute;
  left: 4px;
  bottom: 2px;
`;

const AnswerPickerButton = styled.button`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ hasEmoji }) => (!hasEmoji ? "4px" : "2px")};
  border-radius: 4px;
  font-size: 18px;

  &:hover {
    ${({ hasEmoji }) =>
      !hasEmoji
        ? `
       svg {
         transition: transform 0.2s ease-in-out;
         transform: scale(1.14);
      }
   `
        : `
       span {
         transition: transform 0.2s ease-in-out;
         transform: scale(1.16);
      }
      `};
  }

  svg {
    height: 16px;
    width: 16px;
  }
`;

const EmojiPickerBar = styled.div`
  position: absolute;
  bottom: -42px;
  left: 11px;
  z-index: 1000;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 9px 16px;
  width: max-content;
  border-radius: 0px 25px 25px 25px;
  background: #fff;
  box-shadow: 0px 0px 14.7px 0px rgba(0, 0, 0, 0.19);
`;

const EmojiButton = styled.button`
  display: flex;
  font-size: 20px;
  padding: 0px;
  background: none;
  border: none;
  cursor: pointer;
  transition: transform 0.2s ease-in-out;

  &:hover {
    transform: scale(1.2);
  }
`;

const ClearEmojiButton = styled(EmojiButton)`
  height: 20px;
  padding-top: 1px;
  opacity: 0.8;

  &:hover {
    transform: none;
  }
`;

const emojiList = ["❤️", "👍", "🔥", "🙏", "🥳", "🤔"];

const AnswerEmojiPicker = ({ questionData }) => {
  const emojiButtonRef = useRef(null);
  const emojiBarRef = useRef(null);

  const [showEmojiPickerBar, setShowEmojiPickerBar] = useState(false);
  const [selectedEmoji, setSelectedEmoji] = useState("");

  useEffect(() => {
    if (questionData?.question_answer?.answer_emoji) {
      setSelectedEmoji(questionData?.question_answer?.answer_emoji);
    }
  }, [questionData?.question_answer]);

  const updateAnswerEmoji = useCallback(
    async (emoji) => {
      try {
        await axios.patch(`${BASE_API_URL}/questions/${questionData._id}`, {
          ...questionData,
          question_answer: {
            ...questionData.question_answer,
            answer_emoji: emoji,
          },
        });

        setSelectedEmoji(emoji);
        setShowEmojiPickerBar(false);
      } catch (error) {
        console.error("Update answer emoji error:", error);
      }
    },
    [questionData]
  );

  const handleSelectEmoji = (e, emoji) => {
    e.stopPropagation();
    updateAnswerEmoji(emoji);
  };

  const handleOpenEmojiBar = (e) => {
    e.stopPropagation();
    setShowEmojiPickerBar((prevState) => !prevState);
  };

  useOnclickOutside(() => setShowEmojiPickerBar(false), {
    refs: [emojiButtonRef, emojiBarRef],
  });

  return (
    <AnswerPickerContainer>
      <AnswerPickerButton
        ref={emojiButtonRef}
        hasEmoji={!!selectedEmoji}
        onClick={handleOpenEmojiBar}
      >
        {selectedEmoji ? <span>{selectedEmoji}</span> : <AnswerEmojiIcon />}{" "}
      </AnswerPickerButton>

      {showEmojiPickerBar && (
        <EmojiPickerBar ref={emojiBarRef}>
          <ClearEmojiButton onClick={(e) => handleSelectEmoji(e, "")}>
            <AnswerEmojiIcon />
          </ClearEmojiButton>

          {emojiList.map((emoji) => (
            <EmojiButton
              key={emoji}
              onClick={(e) => handleSelectEmoji(e, emoji)}
            >
              {emoji}
            </EmojiButton>
          ))}
        </EmojiPickerBar>
      )}
    </AnswerPickerContainer>
  );
};

export default memo(AnswerEmojiPicker);
